<template>
  <div>
    <header class="jumbo-page-header">
      <h1 class="locator-title" id="aria-label_locator" tabindex="-1" style="outline: none"> School Locator </h1>
      <p class="locator-subtitle"> Find your home schools if you live within the Mesa Public Schools border area or filter by school type. </p>
    </header>
    <main v-if="!loading.active" role="main" class="container mt-5 mx-auto">
      <div class="locator-container">
        <div class="above-search-container"> </div>
        <div class="above-map-container">
          <div class="map-text text-center">
            <p>Interactive map displays boundary information for the 2023-24 school year.</p>
          </div>
        </div>
        <div class="search-container" ref="searchHeader">
          <form id="searchForm" class="flex-tablet-grow-1" role="search" aria-label="Home School locator" aria-controls="aria-live_results aria-live_error" @submit.prevent="submit">
            <fieldset>
              <legend class="text-center font-family-montserrat is-size-4 mx-desktop-0 pb-2 mb-3" style=""> Address Search </legend>
              <div class="field">
                <div class="control is-expanded">
                  <label for="street" class="label">Your Street Address</label>
                  <input type="text" :class="'input'" id="street" v-model="street" ref="streetInput" placeholder="123 N Home St" required />
                </div>
              </div>
              <div class="field">
                <div class="control is-expanded">
                  <label for="zipcode" class="label">Your Zip Code</label>
                  <input v-on:keyup.enter="submit" type="text" :class="'input'" id="zipcode" v-model="zipcode" placeholder="12345" required />
                </div>
              </div>
              <div class="field is-grouped is-grouped-right pt-3">
                <div class="control">
                  <button class="button is-danger focus-outline-blue" role="button" tabindex="0" @click.prevent="reset(true)"> Reset </button>
                </div>
                <div class="control">
                  <button type="submit" class="button is-success focus-outline-blue">Submit</button>
                </div>
              </div>
            </fieldset>
          </form>
          <div class="
              results-container
              flex-tablet-grow-1
              mt-5 mt-tablet-0 mt-desktop-5
              px-3 px-tablet-5 px-desktop-2
            ">
            <p class="is-size-5 font-family-robo p-3" aria-live="polite" id="aria-live_error" v-if="search.isActive && !search.hasResults && !street.length">
              <i class="far fa-times-circle has-text-danger"> That address is outside our district.</i> Please visit <a class="has-text-weight-semibold" href="http://www.mpsaz.org/parents/open_enroll">our open enrollment page.</a>
            </p>
            <p class="is-size-5 font-family-robo p-3" aria-live="polite" id="aria-live_error" v-if="search.isActive && search.hasResults && partialMatch">
              <i class="fas fa-exclamation-triangle has-text-warning">
                <span class="has-text-danger ml-1">Partial Match...</span>
              </i> There is a problem with your street address or zip code. Double check your zip code and make sure your street address includes the street number (123), direction (N, S, E, W), street name (Elm), and street suffix (st, ave, dr, ln, cir, etc.).
            </p>
            <div v-else-if="search.isActive && search.hasResults" aria-hidden="true">
              <div class="d-flex align-items-center py-3" style="border-bottom: 1px solid black">
                <img src="/images/icons/gmaps/marker-home.png" alt="Google Maps Marker" />
                <p class="pl-3 notranslate">{{ search.userGeocodeResults.address }}</p>
              </div>
              <ul class="py-3" aria-hidden="true">
                <li v-for="school in search.homeschools" :key="school.id" class="d-flex align-items-center py-2">
                  <img :src="`/images/icons/gmaps/${school.gmaps_marker}`" alt="Google Maps Marker" />
                  <p class="px-3 hover-cursor-pointer flex-grow-1 notranslate" @click="toggleHomeSchoolPolygon($event, school)">
                    {{ school.name }}<br />
                    {{ Math.round(school.distance * 100) / 100 }} mi
                  </p>
                  <input :id="`${school.name} boundaries`" name="checkbox" type="checkbox" class="hover-cursor-pointer" @change="toggleHomeSchoolPolygon($event, school)" />
                  <label class="mx-2" aria-label="checkbox" :for="`#${school.name} boundaries`"></label>
                </li>
              </ul>
            </div>
          </div>
          <div v-if="!street.length && !zipcode.length && !search.hasResults" class="">
            <fieldset>
              <legend class="text-center font-family-montserrat is-size-4 mx-desktop-0 pb-2 mb-3"> Type Search </legend>
              <h4 class="ul-title" v-on:click="toggleAccordion('elems')" role="button"> Elementary <span id="elemToggle" :class="!elems
                  ? 'fas fa-toggle-off fa-pull-right fa-fw text-white'
                  : 'fas fa-toggle-on fa-pull-right fa-fw text-white'
                "></span>
              </h4>
              <ul id="elems" :class="elems ? 'ul-body' : 'ul-hide'">
                <li class="ml-2" v-bind:key="school.id" v-for="school in schools.elementary" v-on:click="toggleZoomSchool(school, 'elems')">
                  <a class="notranslate">{{ school.name }}</a>
                </li>
              </ul>
              <h4 class="ul-title" v-on:click="toggleAccordion('jh')" role="button"> Junior High <span id="jhToggle" :class="!jrHighs
                  ? 'fas fa-toggle-off fa-pull-right fa-fw text-white'
                  : 'fas fa-toggle-on fa-pull-right fa-fw text-white'
                "></span>
              </h4>
              <ul id="jh" :class="this.jrHighs ? 'ul-body' : 'ul-hide'" ref="jh">
                <li class="ml-2" v-on:click="toggleZoomSchool(school, 'juniorHighs')" v-bind:key="school.id" v-for="school in schools.junior_high">
                  <a class="notranslate">{{ school.name }}</a>
                </li>
              </ul>
              <h4 class="ul-title" v-on:click="toggleAccordion('hs')" role="button"> Senior High <span id="hsToggle" :class="!this.highSchools
                  ? 'fas fa-toggle-off fa-pull-right fa-fw text-white'
                  : 'fas fa-toggle-on fa-pull-right fa-fw text-white'
                "></span>
              </h4>
              <ul id="hs" :class="this.highSchools ? 'ul-body' : 'ul-hide'" ref="hs">
                <li class="ml-2" v-on:click="toggleZoomSchool(school, 'highSchools')" v-bind:key="school.id" v-for="school in schools.high_school">
                  <a class="notranslate">{{ school.name }}</a>
                </li>
              </ul>
              <h4 class="ul-title" v-on:click="toggleAccordion('choice')" role="button"> Choice <span id="choiceToggle" :class="!this.choice
                  ? 'fas fa-toggle-off fa-pull-right fa-fw text-white'
                  : 'fas fa-toggle-on fa-pull-right fa-fw text-white'
                "></span>
              </h4>
              <ul id="choice" :class="this.choice ? 'ul-body' : 'ul-hide'" ref="choice">
                <li class="ml-2" v-bind:key="school.id" v-for="school in schools.choice" v-on:click="toggleZoomSchool(school, 'choice')">
                  <a class="notranslate">{{ school.name }}</a>
                </li>
              </ul>
            </fieldset>
          </div>
        </div>
        <div class="map-container">
          <gmap-map class="w-100 h-100" :center="mapCenter" :zoom="zoom">
            <div id="elemMap" v-if="elems && !street.length && !zipcode.length">
              <gmap-marker v-for="school in schools.elementary" :key="school.id" :clickable="true" :alt="school.name" @click="toggleTypeInfoWindow(school)" :position="{ lat: school.latitude, lng: school.longitude }" :icon="'/images/icons/gmaps/marker-el.png'" :visible="school.showMapMarker">
                <gmap-info-window :position="{ lat: school.latitude, lng: school.longitude }" :options="infoOptions" :opened="school.showInfoWindow" @closeclick="toggleTypeInfoWindow(school)">
                  <h4>{{ school.name }} {{ school.end_name }}</h4>
                  <p>
                    {{ school.address }}<br />
                    {{ school.city }}, {{ school.state }} {{ school.zipCode }}
                  </p>
                  <a :href="school.url">{{ school.url }}</a>
                </gmap-info-window>
                <gmap-polygon :paths="school.polygons.slice(0, 4)" :editable="false" :options="{
                    visible: school.showPolygons,
                    strokeColor: 'blue',
                    strokeOpacity: 0.8,
                    strokeWeight: 1,
                    fillColor: school.color,
                    fillOpacity: 0.35,
                  }">
                </gmap-polygon>
              </gmap-marker>
            </div>
            <div v-if="this.jrHighs && !this.street.length && !this.zipcode.length">
              <gmap-marker v-for="school in schools.junior_high" :key="school.id" :clickable="true" :alt="school.name" @click="toggleTypeInfoWindow(school)" :position="{ lat: school.latitude, lng: school.longitude }" :icon="'/images/icons/gmaps/marker-jh.png'" :visible="school.showMapMarker">
                <gmap-info-window :position="{ lat: school.latitude, lng: school.longitude }" :options="infoOptions" :opened="school.showInfoWindow" @closeclick="toggleTypeInfoWindow(school)">
                  <h4>{{ school.name }}</h4>
                  <p>
                    {{ school.address }}<br />
                    {{ school.city }}, {{ school.state }} {{ school.zipCode }}
                  </p>
                  <a :href="school.url">{{ school.url }}</a>
                </gmap-info-window>
                <gmap-polygon :paths="getJuniorHighPaths(school.polygons)" :editable="false" :options="{
                    visible: school.showPolygons,
                    strokeColor: 'blue',
                    strokeOpacity: 0.8,
                    strokeWeight: 1,
                    fillColor: school.color,
                    fillOpacity: 0.35,
                  }">
                </gmap-polygon>
              </gmap-marker>
            </div>
            <div v-if="this.highSchools && !this.street.length && !this.zipcode.length">
              <gmap-marker v-for="school in schools.high_school" :key="school.id" :clickable="true" :alt="school.name" @click="toggleTypeInfoWindow(school)" :position="{ lat: school.latitude, lng: school.longitude }" :icon="'/images/icons/gmaps/marker-hs.png'" :visible="school.showMapMarker">
                <gmap-info-window :position="{ lat: school.latitude, lng: school.longitude }" :options="infoOptions" :opened="school.showInfoWindow" @closeclick="toggleTypeInfoWindow(school)">
                  <h4>{{ school.name }}</h4>
                  <p>
                    {{ school.address }}<br />
                    {{ school.city }}, {{ school.state }} {{ school.zipCode }}
                  </p>
                  <a :href="school.url">{{ school.url }}</a>
                </gmap-info-window>
                <gmap-polygon :paths="school.polygons" :editable="false" :options="{
                    visible: school.showPolygons,
                    strokeColor: 'blue',
                    strokeOpacity: 0.8,
                    strokeWeight: 1,
                    fillColor: school.color,
                    fillOpacity: 0.35,
                  }">
                </gmap-polygon>
              </gmap-marker>
            </div>
            <div v-if="this.choice && !this.street.length && !this.zipcode.length">
              <gmap-marker v-for="school in schools.choice" :key="school.id" :clickable="true" :alt="school.name" @click="toggleTypeInfoWindow(school)" :position="{ lat: school.latitude, lng: school.longitude }" :icon="'/images/icons/gmaps/fa-sh.png'" :visible="school.showMapMarker">
                <gmap-info-window :position="{ lat: school.latitude, lng: school.longitude }" :options="infoOptions" :opened="school.showInfoWindow" @closeclick="toggleTypeInfoWindow(school)">
                  <h4>{{ school.name }} {{ school.end_name }}</h4>
                  <p>
                    {{ school.address }}<br />
                    {{ school.city }}, {{ school.state }} {{ school.zipCode }}
                  </p>
                  <a :href="school.url">{{ school.url }}</a>
                </gmap-info-window>
                <gmap-polygon :paths="school.polygons" :editable="false" :options="{
                    visible: school.showPolygons,
                    strokeColor: 'blue',
                    strokeOpacity: 0.8,
                    strokeWeight: 1,
                    fillColor: school.color,
                    fillOpacity: 0.35,
                  }">
                </gmap-polygon>
              </gmap-marker>
            </div>
            <div v-if="search.hasResults && search.isActive && !partialMatch">
              <gmap-marker :position="{
                  lat: search.userGeocodeResults.lat,
                  lng: search.userGeocodeResults.lng,
                }" :clickable="true" @click="showUserInfoWindow = !showUserInfoWindow" :draggable="false" :icon="'/images/icons/gmaps/marker-home.png'">
                <gmap-info-window :position="{
                    lat: search.userGeocodeResults.lat,
                    lng: search.userGeocodeResults.lng,
                  }" :options="infoOptions" :opened="showUserInfoWindow" @closeclick="showUserInfoWindow = false">
                  <h4>{{ search.userGeocodeResults.address }}</h4>
                </gmap-info-window>
              </gmap-marker>
              <gmap-marker v-for="school in search.homeschools" :key="school.id" :clickable="true" @click="toggleHomeSchoolInfoWindow(school)" :position="{ lat: school.latitude, lng: school.longitude }" :icon="'/images/icons/gmaps/' + school.gmaps_marker">
                <gmap-info-window class="notranslate" :position="{ lat: school.latitude, lng: school.longitude }" :options="infoOptions" :opened="school.showInfoWindow" @closeclick="school.showInfoWindow = false">
                  <h4>{{ school.name }}</h4>
                  <p>
                    {{ school.address }}<br />
                    {{ school.city }}, {{ school.state }} {{ school.zipCode }}
                  </p>
                  <div v-show="school.distance" class="distance"> Distance: <span class="inner-distance">{{ Math.round(school.distance * 100) / 100 }} mi</span>
                  </div>
                  <a :href="school.url">{{ school.url }}</a>
                </gmap-info-window>
                <gmap-polygon :paths="school.polygons" :editable="false" :options="{
                    visible: school.showPolygons,
                    strokeColor: 'blue',
                    strokeOpacity: 0.8,
                    strokeWeight: 1,
                    fillColor: school.color,
                    fillOpacity: 0.35,
                  }">
                </gmap-polygon>
              </gmap-marker>
            </div>
          </gmap-map>
        </div>
        <div class="below-search-container"> </div>
        <div class="below-map-text-container"> </div>
      </div>
      <br>
      <!-- <div v-if="search.hasResults && !partialMatch && !zeroResults">
        <div class="sr-only" id="aria-live_results" aria-live="polite" aria-atomic="true">
          {{ ariaText }}
        </div>
        <h2 class="font-family-montserrat is-size-3 mx-3 mx-desktop-0 pb-2 mb-3" style="border-bottom: 1px solid black"> Search Results </h2>
        <transition name="fade">
          <div class="cards-container">
            <cmps-card v-for="school in search.homeschools" :key="school.id" :school="school" :categories="categories" class="locator"></cmps-card>
          </div>
        </transition>
      </div> -->
    </main>
    <div class="text-center my-5 py-5 is-size-4 is-size-2-desktop" v-if="this.loading.active && !this.timeout.active">
      <h1>Loading... <i class="fas fa-spinner fa-spin"></i></h1>
    </div>
    <div v-if="this.timeout.active && this.loading.active" class="text-center my-5 py-5 is-size-4 is-size-2-desktop">
      <h1 style="color: #990022;"> No response from API. <br />Please contact MPS Web Services at <br />
        <a style="color: #112266" href="mailto:web@mpsaz.org">web@mpsaz.org</a> &nbsp;<i style="color: #FFCC33" class="fas fa-envelope"></i>
      </h1>
    </div>
  </div>
</template>

<script>
import Card from "@/components/Card.vue";
import Compare from "@/components/CompareModal.vue";
import { mapMutations } from 'vuex';

export default {
  name: "Locator",
  components: {
    cmpsCard: Card,
    cmpsCompare: Compare,
  },
  data() {
    return {
      pdfFiles: {
        el: '/files/el.pdf',
        jh: '/files/jh.pdf',
        hs: '/files/hs.pdf',
      },
      highSchools: window.location.pathname === "/hs" ? true : false,
      jrHighs: window.location.pathname === "/jh" ? true : false,
      elems: window.location.pathname === "/el" ? true : false,
      choice: false,
      street: "",
      zipcode: "",
      partialMatch: false,
      zeroResults: false,
      search: {
        hasResults: false,
        isActive: false,
        homeschools: [],
        userGeocodeResults: {
          address: "",
          lat: 0,
          lng: 0,
        },
      },
      showUserInfoWindow: false,
      mapCenter: { lat: 33.4304395, lng: -111.74174540000001 },
      zoom: 11,
      infoOptions: {
        pixelOffset: {
          width: 0,
          height: 0,
        },
      },
    };
  },
  computed: {
    timeout() {
      return this.$store.state.main.timeout
    },
    loading() {
      return this.$store.state.main.loading
    },
    categories() {
      return this.$store.state.search.categories;
    },
    schools() {
      return this.$store.getters.categorizedSchools;
    },
    ariaText() {
      const results = this.search.homeschools.map((hs) => `${hs.name} ${hs.end_name}`);
      return `Home schools for ${this.search.userGeocodeResults.address} are: ${results.join(
        ", "
      )}. An interactive google map shows school locations. Below the map is more information on each school`;
    },
  },

  created() {
    const timeout = setTimeout(() => {
      this.toggleTimeout(true);
    }, 20000)

    if (!this.loading.active) {
      this.toggleTimeout(false)
      clearTimeout(timeout)
    }
  },

  mounted() {
    document.getElementById("aria-label_locator").focus();

    const streetInput = document.getElementById('street');

    const zipInput = document.getElementById('zipcode');

    if (zipInput) {
      this.handleZipInput = zipInput.addEventListener("keypress", ev => {
        if (ev.key === "Enter") {
          ev.preventDefault();
        }
      })
    }

    if (streetInput) {
      this.handleStreetInput = streetInput.addEventListener("keypress", ev => {
        if (ev.key === "Enter") {
          ev.preventDefault();
        }
      })
    }

    switch (true) {
      case window.location.pathname === "/el":
        this.schools.elementary.map((school) => this.togglePolygon(school, false));
        this.schools.elementary.map((school) => this.toggleMapMarker(school, false));
        break;
      case window.location.pathname === "/jh":
        this.schools.junior_high.map((school) => this.togglePolygon(school, false));
        this.schools.junior_high.map((school) => this.toggleMapMarker(school, false));
        break;
      case window.location.pathname === "/hs":
        this.schools.high_school.map((school) => this.togglePolygon(school, false));
        this.schools.high_school.map((school) => this.toggleMapMarker(school, false));
        break;
      default:
        break;
    }
  },

  destroyed() {
    const streetInput = document.getElementById('street');

    const zipInput = document.getElementById('zipcode');

    streetInput.removeEventListener('keypress', this.handleStreetInput);

    zipInput.removeEventListener('keypress', this.handleZipInput);
  },

  methods: {
    ...mapMutations([
      'toggleTimeout'
    ]),
    getJuniorHighPaths(polygons){
      const paths = [...polygons];
      paths.splice(3, 1); // Remove the fourth array (schoolPolygonsk6)
      return paths;
    },
    submit() {
      this.reset(false);
      this.$store
        .dispatch("getHomeschools", { address: this.street + ", " + this.zipcode })
        .then((response) => {
          if (response.homeschools.length === 3 || response.homeschools.length === 2) {
            this.mapCenter = {
              lat: response.userData.latLng.lat(),
              lng: response.userData.latLng.lng(),
            };
            this.search = {
              hasResults: true,
              homeschools: response.homeschools,
              userGeocodeResults: {
                address: response.userData.address,
                lat: response.userData.latLng.lat(),
                lng: response.userData.latLng.lng(),
              },
            };
            this.zeroResults = response.userData.zeroResults;
            this.partialMatch = response.userData.partialMatch;
            this.search.homeschools.map((homeSchool) =>
              this.calculateDistance(
                this.search.userGeocodeResults.lat,
                this.search.userGeocodeResults.lng,
                homeSchool.latitude,
                homeSchool.longitude,
                "K",
                homeSchool
              )
            );
          }

          if (!response.homeschools.length || response.userData.zeroResults) {
            this.search.hasResults = false;
            this.street = "";
            this.zipcode = "";
          }

          this.search.isActive = true;
          this.$refs.searchHeader.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
          gtag("event", "find_homeschools", {
            event_category: "locator_interaction",
            event_label: `${this.street} ${this.zipcode}`,
          });
        })
        .catch((error) => {
          console.log(error);
        });
    },
    reset(clearInput) {
      this.search = {
        hasResults: false,
        isActive: false,
        homeschools: [],
        userGeocodeResults: {
          address: "",
          lat: 0,
          lng: 0,
        },
      };
      this.$store.commit("resetCompare");
      if (clearInput) {
        this.street = "";
        this.zipcode = "";
      }
      this.mapCenter = { lat: 33.4304395, lng: -111.74174540000001 };
      this.zoom = 11;
      this.closeAccordion();
    },
    toggleHomeSchoolPolygon(e, school) {
      if (e.target.tagName === "P") {
        school.showPolygons = !e.target.nextSibling.checked;
        e.target.nextSibling.checked = !e.target.nextSibling.checked;
      } else {
        school.showPolygons = e.target.checked;
      }
    },
    toggleHomeSchoolInfoWindow(school) {
      school.showInfoWindow = !school.showInfoWindow;
      this.search.homeschools
        .filter((s) => s.id !== school.id)
        .map((filteredSchool) => this.toggleInfoWindow(filteredSchool, true));
    },
    toggleTypeInfoWindow(school) {
      switch (true) {
        case this.elems && school.showInfoWindow === false:
          school.showInfoWindow = true
          this.schools.elementary
            .filter((s) => s.id !== school.id)
            .map((filteredSchool) => {
              this.togglePolygon(filteredSchool, true)
              this.toggleMapMarker(filteredSchool, true)
              this.toggleInfoWindow(filteredSchool, true)
            });
          break;
        case this.elems && school.showInfoWindow === true:
          school.showInfoWindow = false
          this.schools.elementary
            .filter((s) => s.id !== school.id)
            .map((filteredSchool) => {
              this.togglePolygon(filteredSchool, false)
              this.toggleMapMarker(filteredSchool, false)
              this.toggleInfoWindow(filteredSchool, true)
            });
          break;
        case this.jrHighs && school.showInfoWindow === true:
          school.showInfoWindow = false
          this.schools.junior_high
            .filter((s) => s.id !== school.id)
            .map((filteredSchool) => {
              this.togglePolygon(filteredSchool, false)
              this.toggleMapMarker(filteredSchool, false)
              this.toggleInfoWindow(filteredSchool, true)
            });
          break;
        case this.jrHighs && school.showInfoWindow === false:
          school.showInfoWindow = true
          this.schools.junior_high
            .filter((s) => s.id !== school.id)
            .map((filteredSchool) => {
              this.togglePolygon(filteredSchool, true)
              this.toggleMapMarker(filteredSchool, true)
              this.toggleInfoWindow(filteredSchool, true)
            });
          break;
        case this.highSchools && school.showInfoWindow === true:
          school.showInfoWindow = false
          this.schools.high_school
            .filter((s) => s.id !== school.id)
            .map((filteredSchool) => {
              this.togglePolygon(filteredSchool, false)
              this.toggleMapMarker(filteredSchool, false)
              this.toggleInfoWindow(filteredSchool, true)
            });
          break;
        case this.highSchools && school.showInfoWindow === false:
          school.showInfoWindow = true
          this.schools.high_school
            .filter((s) => s.id !== school.id)
            .map((filteredSchool) => {
              this.togglePolygon(filteredSchool, true)
              this.toggleMapMarker(filteredSchool, true)
              this.toggleInfoWindow(filteredSchool, true)
            });
          break;
        case this.choice && school.showInfoWindow === true:
          school.showInfoWindow = false
          this.schools.choice
            .filter((s) => s.id !== school.id)
            .map((filteredSchool) => {
              this.togglePolygon(filteredSchool, false)
              this.toggleMapMarker(filteredSchool, false)
              this.toggleInfoWindow(filteredSchool, true)
            });
          break;
        case this.choice && school.showInfoWindow === false:
          school.showInfoWindow = true
          this.schools.choice
            .filter((s) => s.id !== school.id)
            .map((filteredSchool) => {
              this.togglePolygon(filteredSchool, true)
              this.toggleMapMarker(filteredSchool, true)
              this.toggleInfoWindow(filteredSchool, true)
            });
          break;
        default:
          break;
      }
    },
    togglePolygon(school, isVisible) {
      if (isVisible) {
        school.showPolygons = false;
      } else if (!isVisible) {
        school.showPolygons = true;
      }
    },
    toggleInfoWindow(school, isVisible) {
      if (isVisible) {
        school.showInfoWindow = false;
      } else if (!isVisible) {
        school.showInfoWindow = true;
      }
    },
    toggleMapMarker(school, isVisible) {
      if (isVisible) {
        school.showMapMarker = false;
      } else if (!isVisible) {
        school.showMapMarker = true;
      }
    },
    closeAccordion() {
      this.schools.elementary.map((school) => this.toggleInfoWindow(school, true));
      this.schools.junior_high.map((school) => this.toggleInfoWindow(school, true));
      this.schools.high_school.map((school) => this.toggleInfoWindow(school, true));
      this.schools.choice.map((school) => this.toggleInfoWindow(school, true));
      this.schools.elementary.map((school) => this.togglePolygon(school, true));
      this.schools.junior_high.map((school) => this.togglePolygon(school, true));
      this.schools.high_school.map((school) => this.togglePolygon(school, true));
      this.schools.choice.map((school) => this.togglePolygon(school, true));
      this.schools.elementary.map((school) => this.toggleMapMarker(school, true));
      this.schools.junior_high.map((school) => this.toggleMapMarker(school, true));
      this.schools.high_school.map((school) => this.toggleMapMarker(school, true));
      this.schools.choice.map((school) => this.toggleMapMarker(school, true));
      this.elems = false;
      this.jrHighs = false;
      this.highSchools = false;
      this.choice = false;
    },
    toggleAccordion(type) {
      this.search.isActive = false;
      switch (true) {
        case type === "elems" && !this.elems:
          this.elems = true;
          this.jrHighs = false;
          this.highSchools = false;
          this.choice = false;
          this.schools.elementary.map((school) => this.togglePolygon(school, false));
          this.schools.junior_high.map((school) => this.toggleInfoWindow(school, true));
          this.schools.high_school.map((school) => this.toggleInfoWindow(school, true));
          this.schools.choice.map((school) => this.toggleInfoWindow(school, true));
          this.schools.elementary.map((school) => this.toggleMapMarker(school, false));
          break;
        case type === "elems" && this.elems:
          this.elems = false;
          this.schools.elementary.map((school) => this.toggleInfoWindow(school, true));
          this.schools.elementary.map((school) => this.toggleMapMarker(school, true));
          break;
        case type === "jh" && !this.jrHighs:
          this.jrHighs = true;
          this.elems = false;
          this.highSchools = false;
          this.choice = false;
          this.schools.junior_high.map((school) => this.togglePolygon(school, false));
          this.schools.high_school.map((school) => this.toggleInfoWindow(school, true));
          this.schools.elementary.map((school) => this.toggleInfoWindow(school, true));
          this.schools.choice.map((school) => this.toggleInfoWindow(school, true));
          this.schools.junior_high.map((school) => this.toggleMapMarker(school, false));
          break;
        case type === "jh" && this.jrHighs:
          this.jrHighs = false;
          this.schools.junior_high.map((school) => this.toggleInfoWindow(school, true));
          break;
        case type === "hs" && !this.highSchools:
          this.highSchools = true;
          this.jrHighs = false;
          this.elems = false;
          this.choice = false;
          this.schools.high_school.map((school) => this.togglePolygon(school, false));
          this.schools.junior_high.map((school) => this.toggleInfoWindow(school, true));
          this.schools.elementary.map((school) => this.toggleInfoWindow(school, true));
          this.schools.choice.map((school) => this.toggleInfoWindow(school, true));
          this.schools.high_school.map((school) => this.toggleMapMarker(school, false));
          break;
        case type === "hs" && this.highSchools:
          this.highSchools = false;
          this.schools.high_school.map((school) => this.toggleInfoWindow(school, true));
          break;
        case type === "choice" && this.choice:
          this.choice = false;
          this.schools.choice.map((school) => this.toggleInfoWindow(school, true));
          break;
        case type === "choice" && !this.choice:
          this.elems = false;
          this.jrHighs = false;
          this.highSchools = false;
          this.choice = true;
          this.schools.choice.map((school) => this.togglePolygon(school, false));
          this.schools.junior_high.map((school) => this.toggleInfoWindow(school, true));
          this.schools.high_school.map((school) => this.toggleInfoWindow(school, true));
          this.schools.elementary.map((school) => this.toggleInfoWindow(school, true));
          this.schools.choice.map((school) => this.toggleMapMarker(school, false));
          break;
        default:
          break;
      }
      this.mapCenter = { lat: 33.4304395, lng: -111.74174540000001 };
      this.zoom = 11;
    },
    toggleZoomSchool(school, type) {
      school.showInfoWindow = !school.showInfoWindow;
      school.showMapMarker = true;
      this.mapCenter = { lat: school.latitude, lng: school.longitude };
      this.zoom = 11;
      switch (true) {
        case type === "elems":
          this.schools.elementary
            .filter((s) => s.id !== school.id)
            .map((s) => this.toggleInfoWindow(s, true));
          break;
        case type === "juniorHighs":
          this.schools.junior_high
            .filter((s) => s.id !== school.id)
            .map((s) => this.toggleInfoWindow(s, true));
          break;
        case type === "highSchools":
          this.schools.high_school
            .filter((s) => s.id !== school.id)
            .map((s) => this.toggleInfoWindow(s, true));
          break;
        case type === "choice":
          this.schools.choice
            .filter((s) => s.id !== school.id)
            .map((s) => this.toggleInfoWindow(s, true));
          break;
        default:
          break;
      }
    },
    calculateDistance(lat1, lon1, lat2, lon2, unit, school) {
      let radlat1 = (Math.PI * lat1) / 180;
      let radlat2 = (Math.PI * lat2) / 180;
      let theta = lon1 - lon2;
      let radtheta = (Math.PI * theta) / 180;
      let dist =
        Math.sin(radlat1) * Math.sin(radlat2) +
        Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
      dist = Math.acos(dist);
      dist = (dist * 180) / Math.PI;
      dist = dist * 60 * 1.1515;
      if (unit == "K") {
        dist = dist * 1.609344;
      }
      if (unit == "N") {
        dist = dist * 0.8684;
      }
      school.distance = dist;
      return school;
    },
  },
};
</script>

<style lang="scss" scoped>
@keyframes Reveal {
  from {
    max-height: 0px;
  }

  to {
    max-height: 75px;
  }
}

@keyframes Conceal {
  from {
    max-height: 75px;
  }

  to {
    max-height: 0px;
  }
}

@keyframes FadeIn {
  25% {
    opacity: 0;
  }

  50% {
    opacity: 0.5;
  }

  100% {
    opacity: 1;
  }
}

@keyframes FadeOut {
  25% {
    opacity: 1;
  }

  50% {
    opacity: 0.5;
  }

  100% {
    opacity: 0;
  }
}

a,
p {
  word-wrap: break-word;
}

a:hover {
  color: darkred;
  text-decoration-line: underline;
  text-decoration-color: gold;
}

legend {
  text-decoration-line: underline;
}

.distance {
  font-weight: 400;
}

.inner-distance {
  font-weight: lighter;
}

#elemToggle,
#jhToggle,
#hsToggle,
#choiceToggle {
  line-height: unset;
}

.input.bg-success:focus {
  border: 2px solid lawngreen;
}

.input.bg-danger:focus {
  border: 2px solid tomato;
}

.help-success {
  color: green;
}

.help-danger {
  color: red;
}

.button.is-danger {
  background: darkred;
  text-shadow: 0.5px 0.5px 0.5px black;
  font-weight: bolder;
}

.button.is-success {
  background: darkgreen;
  text-shadow: 0.5px 0.5px 0.5px black;
  font-weight: bolder;
}

.is-danger:hover {
  color: yellow;
  background: rgb(182, 1, 1);
}

.is-success:hover {
  color: yellow;
  background: rgb(1, 141, 1);
}

fieldset {
  border: none;
}

.marker-wrapper {
  animation: FadeIn 5s both;
}

.ul-body {
  max-height: 75px;
  overflow: scroll;
  overflow-x: hidden;
  background: white;
  animation: Reveal 0.5s, FadeIn 0.5s;
  text-shadow: 0.5px 0.5px 0.5px black;
}

.ul-hide {
  max-height: 0px;
  overflow: scroll;
  overflow-x: hidden;
  background: rgba(29, 28, 28, 0.445);
  animation: Conceal 0.5s, FadeOut 0.5s;
}

.ul-title {
  position: sticky;
  margin-top: 3px;
  margin-bottom: 3px;
  background: #126;
  color: whitesmoke;
  padding: 7px 7px 7px 7px;
  cursor: pointer;
  border: 1px solid transparent;
  font-weight: bold;
  text-shadow: 1px 1px 1px black;
}

.ul-title:hover {
  background: rgb(86, 98, 150);
  border: 1px solid transparent;
  color: yellow;
}

.locator-title {
  margin-left: 25px;
}

.locator-subtitle {
  margin-left: 25px;
  padding-bottom: 10px;
  font-size: small;
}

.cards-container {
  width: 300px;
  padding: 0.75rem 0;
  margin-left: auto;
  margin-right: auto;
  -webkit-overflow-scrolling: touch;

  @media screen and (min-width: 400px) {
    //when 1 card(360px width + 24px margins) will fit on screen
    width: 384px;
  }

  @media screen and (min-width: 768px) {
    //when 2 cards(360px width + 24px margins) will fit on screen
    width: 768px;
  }

  @media screen and (min-width: 1024px) {
    //when 3 cards(315px width + 20px margins) will fit on screen
    width: 960px;
  }

  @media screen and (min-width: 1216px) {
    //when 3 cards(360px width + 24px margins) will fit on screen
    width: 1152px;
  }
}

.locator-container {
  display: grid;
  grid-template-columns: 27% 1% 72%;
  grid-template-areas:
    "above-search . above-map"
    "search . map"
    "below-search . below-map";

  .above-search-container {
    grid-area: above-search;
  }

  .above-map-container {
    grid-area: above-map;
  }

  .search-container {
    grid-area: search;
    background-color: beige;
    padding: 10px;
  }

  .map-container {
    grid-area: map;
    height: 666px;
  }

  .below-search-container {
    grid-area: below-search;
  }

  .below-map-text-container {
    grid-area: below-map;
  }
}

@media (max-width: 768px) {
  .locator-container {
    padding: 10px;
    grid-template-columns: 100%;
    grid-template-areas:
      "below-map"
      "search"
      "above-map"
      "map"
  }

  .above-map-container {
    margin-top: 35px;
  }

  .map-container {
    height: 350px;
  }
}
</style>
